import React, { useMemo, useState } from 'react'
import {
  ChartFrame,
  LINE_CHART_DEFAULT_HEIGHT,
  LINE_CHART_DEFAULT_SERIES,
  LINE_CHART_DEFAULT_WIDTH,
  LineChart,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './LineChartShowcase.css'

const SAMPLE_X_LABELS = LINE_CHART_DEFAULT_SERIES[0].data.map(
  (point) => point.label ?? '',
)

const SAMPLE_LEGEND = LINE_CHART_DEFAULT_SERIES.map((item) => ({
  label: item.name,
  color: item.color ?? 'var(--ds-blue-500)',
}))

const LineChartShowcase: React.FC = () => {
  const [showXAxisLabel, setShowXAxisLabel] = useState(false)
  const [showYAxisLabel, setShowYAxisLabel] = useState(false)
  const [width, setWidth] = useState(LINE_CHART_DEFAULT_WIDTH)
  const [height, setHeight] = useState(LINE_CHART_DEFAULT_HEIGHT)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'line-chart-width',
            label: `Width (${width}px)`,
            control: (
              <input
                id="line-chart-width"
                className="ui-field-control"
                type="range"
                min={280}
                max={800}
                step={8}
                value={width}
                onChange={(event) => setWidth(Number(event.target.value))}
              />
            ),
          },
          {
            id: 'line-chart-height',
            label: `Height (${height}px)`,
            control: (
              <input
                id="line-chart-height"
                className="ui-field-control"
                type="range"
                min={120}
                max={320}
                step={8}
                value={height}
                onChange={(event) => setHeight(Number(event.target.value))}
              />
            ),
          },
          {
            id: 'line-chart-x-title',
            label: 'X axis title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="line-chart-x-title"
                  type="checkbox"
                  checked={showXAxisLabel}
                  onChange={(event) => setShowXAxisLabel(event.target.checked)}
                />
                <span>Show “Day”</span>
              </label>
            ),
          },
          {
            id: 'line-chart-y-title',
            label: 'Y axis title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="line-chart-y-title"
                  type="checkbox"
                  checked={showYAxisLabel}
                  onChange={(event) => setShowYAxisLabel(event.target.checked)}
                />
                <span>Show</span>
              </label>
            ),
          },
        ],
      },
    ],
    [height, showXAxisLabel, showYAxisLabel, width],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Line chart"
        description="Multi-series stroke chart — framed with sample X/Y axes and legend. Pass `series` for 2+ lines."
        stageClassName="ui-preview__stage--wide line-chart-showcase__stage-wrap"
      >
        <div className={`line-chart-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Sample weekly trends (3 series) · {width}×{height}px
            </p>
            <div
              className="line-chart-showcase__live-frame"
              style={{ width, maxWidth: '100%' }}
            >
              <ChartFrame
                plotHeight={height}
                yTicks={['80', '60', '40', '20', '0']}
                yAxisLabel="Count"
                showYAxisLabel={showYAxisLabel}
                xLabels={SAMPLE_X_LABELS}
                xAxisLabel="Day"
                showXAxisLabel={showXAxisLabel}
                legend={SAMPLE_LEGEND}
              >
                <LineChart
                  series={LINE_CHART_DEFAULT_SERIES}
                  width={width}
                  height={height}
                  maxValue={80}
                  aria-label="Sample weekly visits, new patients, and recalls"
                />
              </ChartFrame>
            </div>
          </section>

          <section className="line-chart-showcase__matrix">
            <h4 className="line-chart-showcase__section-title">Variants</h4>
            <div
              className={`line-chart-showcase__row line-chart-showcase__row--selected`}
            >
              <span className="line-chart-showcase__row-label">
                Multi-series (3)
              </span>
              <div className="line-chart-showcase__row-preview">
                <LineChart series={LINE_CHART_DEFAULT_SERIES} maxValue={80} />
              </div>
            </div>
            <div className={`line-chart-showcase__row`}>
              <span className="line-chart-showcase__row-label">
                Single series
              </span>
              <div className="line-chart-showcase__row-preview">
                <LineChart />
              </div>
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Line chart"
        sections={propertySections}
      />
    </>
  )
}

export default LineChartShowcase
